import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { Col, Row } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';

import OnTraccrButton from '../common/buttons/OnTraccrButton';
import CustomConfirmModal from '../common/modals/CustomConfirmModal';
import AddEditCertification from './AddEditCertification';
import getCertificationColumns from './CertificationsColumns';
import useToggle from '../common/hooks/useToggle';
import { getCustomFieldTableColumns } from '../forms/formHelpers';
import CertificationsTable from './CertificationsTable';
import FormDetailView from '../forms/CompletedForms/FormDetailView';
import {
  canManageCertifications,
  canAddCertifications,
} from '../users/userHelpers';

import {
  getAllCertificationsForEntity,
  deleteCertification,
  getCertificationCustomFieldTemplate,
} from './state/certifications.actions';
import { getFormById } from '../forms/state/forms.actions';
import { getIdMap } from '../helpers/helpers';

export default function Certifications({
  entityType,
  id,
  isDisplay,
}) {
  const dispatch = useDispatch();
  const { isToggled: isExpanded, toggle: toggleExpanded } = useToggle(true);

  const users = useSelector((state) => state.users.users);

  const [showFormDetail, setShowFormDetail] = useState(false);

  const {
    [entityType]: {
      certifications = [],
      certificationCustomFieldTemplate,
    } = {},
  } = useSelector((state) => state.certifications);

  const [
    isAddEditCertificationVisible,
    setIsAddEditCertificationVisible,
  ] = useState(false);
  const [selectedCertification, setSelectedCertification] = useState({});
  const [isDrawerDisplay, setIsDrawerDisplay] = useState(false);

  const userMap = useMemo(() => getIdMap(users), [users]);
  const targetUser = useMemo(() => userMap[id], [userMap, id]);

  const getCanManage = useCallback(() => {
    if (isDisplay) return false;
    return canManageCertifications(entityType, targetUser);
  }, [entityType, isDisplay, targetUser]);

  const getCanAdd = useCallback(() => {
    if (isDisplay) return false;
    return canAddCertifications(entityType, targetUser);
  }, [entityType, isDisplay, targetUser]);

  const canManage = getCanManage();
  const canAdd = getCanAdd();

  useEffect(() => {
    dispatch(getCertificationCustomFieldTemplate(entityType));
  }, [entityType]);

  useEffect(() => {
    dispatch(getAllCertificationsForEntity(entityType, id));
  }, [entityType, id]);

  const onClose = useCallback(() => {
    setSelectedCertification({});
    setIsAddEditCertificationVisible(false);
    setIsDrawerDisplay(false);
  }, []);

  const onUpdateClick = (record) => {
    setSelectedCertification(record);
    setIsDrawerDisplay(false);
    setIsAddEditCertificationVisible(true);
  };

  const onViewClick = useCallback((record) => {
    setSelectedCertification(record);
    setIsDrawerDisplay(true);
    setIsAddEditCertificationVisible(true);
  }, []);

  const onDeleteClick = useCallback((certification) => new Promise((resolve) => {
    if (!canManage) {
      resolve();
      return;
    }
    CustomConfirmModal({
      title: 'Delete Certification',
      content: (
        <p>
          Are you sure you wish to delete this certification?
        </p>
      ),
      okText: 'Delete',
      cancelText: 'Cancel',
      async onOk() {
        resolve(await dispatch(deleteCertification(entityType, id, certification.id)));
        onClose();
      },
      onCancel() {
        resolve();
      },
    });
  }), [canManage, entityType, id, onClose]);

  const handleFormClick = useCallback((formId) => {
    dispatch(getFormById(formId));
    setShowFormDetail(true);
  }, []);

  const tableColumns = useMemo(() => getCertificationColumns({
    entityType,
    getCanManage,
    onUpdateClick,
    onDeleteClick,
    onViewClick,
    onFormClick: handleFormClick,
  }), [getCanManage, onUpdateClick, onDeleteClick, onViewClick, entityType, handleFormClick]);

  const onAddButtonClick = () => {
    setSelectedCertification({});
    setIsDrawerDisplay(false);
    setIsAddEditCertificationVisible(true);
  };

  const customFieldColumns = useMemo(() => (
    getCustomFieldTableColumns(certificationCustomFieldTemplate?.sections ?? [])
  ), [certificationCustomFieldTemplate]);

  return (
    <>
      <Row justify="space-between" align="middle">
        <Col>
          {canAdd && (
            <OnTraccrButton
              title="Add Certification"
              onClick={onAddButtonClick}
              className="user-add-certification-button"
            />
          )}
        </Col>
        <Col>
          <OnTraccrButton
            title={isExpanded ? 'Collapse All' : 'Expand All'}
            onClick={toggleExpanded}
            icon={isExpanded ? <CaretUpOutlined /> : <CaretDownOutlined />}
            iconLeft
          />
        </Col>
      </Row>
      <CertificationsTable
        certifications={certifications}
        columns={tableColumns}
        isExpanded={isExpanded}
        customFieldColumns={customFieldColumns}
        entityType={entityType}
      />
      <AddEditCertification
        visible={isAddEditCertificationVisible}
        onClose={onClose}
        id={id}
        certification={selectedCertification}
        certificationId={selectedCertification.id}
        onDeleteClick={onDeleteClick}
        entityType={entityType}
        isDisplay={isDrawerDisplay}
      />
      <FormDetailView
        visible={showFormDetail}
        onClose={() => setShowFormDetail(false)}
        shouldRedirect={false}
        userMap={userMap}
        startOnFields
      />
    </>
  );
}

Certifications.propTypes = {
  entityType: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  isDisplay: PropTypes.bool,
};

Certifications.defaultProps = {
  isDisplay: false,
};
